'use client';

import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { CountdownTimer } from 'assets/icons/CountdownTimer';
import { useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { getDiscountForStore } from 'content/products/ring-pro/tierPricing';
import type { TierCampaignData } from './utils/types';
import styles from './TierCampaignChip.module.css';

interface TierCampaignChipProps {
  tierCampaign: TierCampaignData;
  basePrice: number;
  currencySymbol: string | ReactNode;
  store: string;
  className?: string;
}

const GOLD = '#AF8C00';

export function TierCampaignChip({
  tierCampaign,
  basePrice,
  currencySymbol,
  store,
  className,
}: TierCampaignChipProps) {
  const t = useTranslations('ring-pro');
  const formatPriceWithComma = useFormatPrice();

  if (!tierCampaign || !tierCampaign.campaign_active) return null;

  const activeTier = tierCampaign.tier_info[tierCampaign.active_tier];
  if (!activeTier) return null;

  const discount = getDiscountForStore(activeTier.discount_value, store);
  const tierPrice = basePrice - discount;
  const hasSpots = activeTier.max_order_count !== null;
  const spotsLeft = hasSpots
    ? (activeTier.max_order_count ?? 0) - tierCampaign.total_orders
    : 0;

  return (
    <div className={[styles.wrapper, className].filter(Boolean).join(' ')}>
      <span className={styles.tierText}>
        {activeTier.title} {t('ring-pro.tierCampaign.at')}{' '}
        {discount > 0 && (
          <><span className={styles.strikethroughPrice}>
            {currencySymbol}
            {formatPriceWithComma(basePrice)}
          </span>{' '}</>
        )}
        {currencySymbol}
        {formatPriceWithComma(tierPrice)}
      </span>
      {hasSpots && (
        <>
          <span className={styles.dot}>&bull;</span>
          <span className={styles.spotsLeft}>
            <CountdownTimer fill={GOLD} />
            {formatPriceWithComma(spotsLeft)} {t('ring-pro.tierCampaign.left')}
          </span>
        </>
      )}
    </div>
  );
}
